import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { matchPath } from 'react-router-dom-v5-compat';

import { Box } from '~/eds/src/components/Box/Box';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Popover } from '~/eds/src/components/Popover/Popover';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { RoutePathType } from '~/routing/enums';
import { getApplicationRoute } from '~/routing/utils';

import { Citation as CitationType } from '../../../types/chat';
import { CitationButton } from './CitationButton';
import { CitationDetails } from './CitationDetails';

interface Props {
  mode?: 'inline';
  citation: CitationType;
  text: string;
  orientation?: 'horizontal' | 'vertical';
  onClick: (citation: CitationType) => void;
}

export const Citation = ({
  citation,
  mode,
  text,
  orientation = 'vertical',
  onClick,
}: Props) => {
  const [isHovered, _toggleHover, hover, unhover] = useToggle(false);
  const [resolveCitation, { requestId, error, isLoading }] =
    api.endpoints.resolveCitation.useLazyQuery();
  const citationEntity = getCitationEntity(citation);

  const selector = useMemo(
    () => api.endpoints.resolveCitation.select(citationEntity),
    [requestId],
  );
  const handleClick = () => {
    onClick(citation);
  };
  const isInline = mode === 'inline';
  const isVertical = orientation === 'vertical';
  const hasDescription = Boolean(citation.entityName) && !isInline;

  const cachedData = useSelector(selector);
  const resolvedCitation = cachedData.data;

  const onPopoverShow = () => {
    hover();
    if (!resolvedCitation) {
      resolveCitation(getCitationEntity(citation));
    }
  };

  const citationBtn = (
    <Popover
      preset="popover-hover"
      placement="bottom-start"
      onShow={onPopoverShow}
      onHide={unhover}
      offset={[0, 5]}
      maxWidth={500}
      trigger={
        <CitationButton
          onClick={handleClick}
          text={text}
          isActive={isHovered}
        />
      }
    >
      <CitationDetails
        text={citation.tooltip ?? ''}
        isLoading={isLoading}
        resolvedCitation={resolvedCitation}
        error={coerceRtkqError(error)}
      />
    </Popover>
  );

  return isInline ? (
    <Box display="inline-block">{citationBtn}</Box>
  ) : (
    <Layout w={isVertical ? '100%' : undefined} spacing={2} align="center">
      {citationBtn}
      {hasDescription && (
        <Layout w="100%" direction="column">
          <TruncateText w="90%" variant="tiny">
            {citation.entityName}
          </TruncateText>
        </Layout>
      )}
    </Layout>
  );
};

const getCitationEntity = (citation: CitationType): CitationType['entity'] => {
  if (!citation.entity) return null;

  switch (citation.entity.type) {
    case 'ticket':
      return citation.entity;
    default:
    case 'document_id':
      const matchedPath = matchPath(
        RoutePathType.Document,
        getApplicationRoute(citation.link ?? ''),
      );
      return {
        type: 'document_handler',
        id: `${matchedPath?.params.documentId}`,
      };
  }
};
